import React, { memo, useMemo, useCallback, useEffect, useState } from 'react';
import {
    ProfileRecord,
    ResolvedRecord,
    ResolvedSubmission,
} from '~/util/types/submissions';
import {
    BaseLevel,
    CountryClanProfileMergedLevel,
    ProfileMergedLevel,
} from '~/util/types/levels';
import {
    camelToSpaces,
    cn,
    formatLevelGDID,
    getCardLevelThumbnail,
    getCompletionTime,
    getPackThumbnail,
    statusBgClass,
} from '~/util';
import { Pack, PackLevel, ProfilePack } from '~/util/types/packs';
import { CircleCheckBig, InfoIcon, Smartphone } from 'lucide-react';
import { useSettingsStore } from '~/util/stores/settings';
import ApiErrorComponent from '~/partials/ApiErrorComponent';
import Load from './Load';
import { packsQuery } from '~/util/queries/public/other';
import { useQuery } from '@tanstack/react-query';
import { VirtualizedScrollArea } from './VirtualizedScrollArea';
import { useList } from '~/util/hooks/useList';
import { Link, useNavigate } from '@tanstack/react-router';
import { useLocaleDateString } from '~/util/hooks/useLocale';
import { Tooltip, TooltipContent, TooltipTrigger } from '~/primitives/tooltip';
import { UserTags } from '~/partials/Tags';
import { Separator } from '~/primitives/separator';
import { BaseUser } from '~/util/types/users';
import { VideoIcon } from './VideoIcon';
import { CountryClanProfileResolvedRecord } from '~/util/types/profiles';
import { useResettableStateWithKey } from '~/util/hooks/useResettableState';
import { ScrollArea } from '~/primitives/scroll-area';
import { useAppForm } from '~/util/hooks/useAppForm';
import { useSelector } from '@tanstack/react-store';

type AvailableLevelType =
    ProfileMergedLevel | CountryClanProfileMergedLevel | PackLevel;

type AvailableRecordSubmissionType =
    | ResolvedSubmission
    | ResolvedRecord
    | ProfileRecord
    | CountryClanProfileResolvedRecord;

type AvailableDataEntryWithLevel =
    AvailableLevelType | AvailableRecordSubmissionType;

type AvailableDataEntry = AvailableDataEntryWithLevel | ProfilePack;

interface TableWithThumbnailsProps {
    data: AvailableDataEntry[];
    height?: string | number;
    scrollElement?: HTMLDivElement | null;
    showPoints?: boolean;
    showMobile?: boolean;
    enableSearch?: boolean;
    enableSort?: boolean;
    showUserLink?: boolean;
    showUserRole?: boolean;
    showCompletionLink?: boolean;
    showCompletedByUser?: boolean;
    showLastUpdated?: boolean;
    showStatus?: boolean;
    minHeight?: boolean;
    onRowClick?: (data: AvailableDataEntry) => void;
}

const getLevel = (entry: AvailableDataEntryWithLevel) => {
    return 'level' in entry ? entry.level : entry;
};

function MobileIcon({ record }: { record: AvailableRecordSubmissionType }) {
    return (
        record.mobile && (
            <div className="relative inline-block transition-transform align-middle">
                <div
                    className="absolute text-black blur-sm z-0"
                    aria-hidden="true"
                >
                    <Smartphone size={64} />
                </div>
                <div className="relative z-10">
                    <Smartphone size={56} />
                </div>
            </div>
        )
    );
}

function CompletionLinkIcon({
    record,
}: {
    record: AvailableRecordSubmissionType;
}) {
    return (
        <div className="relative inline-block hover:scale-105 transition-transform align-middle">
            <div className="absolute text-black blur-sm z-0" aria-hidden="true">
                <VideoIcon size={64} url={record.video_url} />
            </div>
            <a
                href={record.video_url}
                target="_blank"
                className="relative z-10"
                aria-label="Open completion video"
                onClick={(e) => e.stopPropagation()}
                rel="noreferrer"
            >
                <VideoIcon size={56} url={record.video_url} />
            </a>
        </div>
    );
}

function CompletedByUserIcon({ level }: { level: PackLevel }) {
    return (
        level.completed_by_user && (
            <div className="relative inline-block hover:scale-105 transition-transform align-middle">
                <div
                    className="absolute text-black blur-sm z-0"
                    aria-hidden="true"
                >
                    <CircleCheckBig size={44} />
                </div>
                <div className="relative z-10">
                    <CircleCheckBig size={40} />
                </div>
            </div>
        )
    );
}

function LevelInfo({
    level,
    raw_data,
    showPoints,
    showDetails,
}: {
    level: BaseLevel;
    raw_data: AvailableDataEntryWithLevel;
    showPoints?: boolean;
    showDetails?: boolean;
}) {
    const localeDateString = useLocaleDateString();
    const pointsDetails =
        showDetails && level && showPoints && 'points' in level
            ? level.status == 'MainList'
                ? `+${level.points / 10} points`
                : level.status
            : undefined;

    const completionDetails =
        showDetails && 'completion_time' in raw_data
            ? `Time: ${getCompletionTime(raw_data.completion_time ?? 0)}`
            : showDetails && 'achieved_at' in raw_data
              ? `${localeDateString(raw_data.achieved_at)}`
              : undefined;

    const detailsLabel =
        showDetails &&
        (pointsDetails || completionDetails) &&
        (pointsDetails ?? '') +
            (pointsDetails && completionDetails ? ' | ' : '') +
            (completionDetails ?? '');

    return (
        <Link
            activeOptions={{ includeSearch: false }}
            to="/list/$levelid"
            params={{
                levelid: formatLevelGDID(level),
            }}
            search={(prev) => ({ list: prev.list })}
        >
            <div className="flex items-center gap-2 clamp-[min-h-12-24-clamp]">
                <p className="clamp-[text-xs-2xl-clamp] font-medium text-shadow-3 clamp-[w-8-24-clamp] text-center">
                    {level.status == 'MainList' ? `#${level.position}` : '-'}
                </p>
                <div>
                    <h3 className="clamp-[text-xs-2xl-clamp] font-medium transition-transform hover:scale-105 text-left text-shadow-3">
                        {level.name}
                    </h3>
                    <p className="text-shadow-3 clamp-[text-xs-lg-clamp]">
                        {detailsLabel}
                    </p>
                </div>
            </div>
        </Link>
    );
}

function PackInfo({
    pack,
    showPoints,
    showDetails,
}: {
    pack: Pack;
    showPoints?: boolean;
    showDetails?: boolean;
}) {
    return (
        <Link
            to={`/packs/$packid`}
            params={{ packid: pack.id }}
            activeOptions={{ includeSearch: false }}
        >
            <div className="clamp-[min-h-12-24-clamp] flex flex-col justify-center px-2">
                <h3 className="clamp-[text-xs-2xl-clamp] w-max font-medium transition-transform hover:scale-105 text-left text-shadow-3">
                    {pack.name}
                </h3>

                {showDetails && showPoints && (
                    <p className="text-shadow-3 clamp-[text-xs-lg-clamp]">
                        {pack.points ? `+${pack.points / 10} points` : ''}
                    </p>
                )}
            </div>
        </Link>
    );
}

function UserInfo({
    record,
    showUserLink,
    showUserRole,
}: {
    record: AvailableDataEntryWithLevel;
    showUserLink?: boolean;
    showUserRole?: boolean;
}) {
    const verified =
        'is_verification' in record
            ? record.is_verification
            : 'verified' in record
              ? record.verified
              : false;
    const published = 'published' in record ? record.published : false;
    const created = 'created' in record ? record.created : false;

    const publisher = 'publisher' in record ? record.publisher : null;
    const creators = 'creators' in record ? record.creators : [];

    const isSameUser = (a: BaseUser | null, b: BaseUser | null) =>
        !!a && !!b && a.id === b.id;

    const uniqueCreators = creators.filter(
        (creator) => !isSameUser(creator, publisher),
    );

    const userRoleLabel = verified
        ? 'Verifier'
        : published
          ? !created
              ? 'Publisher'
              : 'Creator & Publisher'
          : created
            ? 'Creator'
            : null;

    const userCountryClanLabel = verified
        ? 'Verified by:'
        : publisher
          ? creators.length == 0
              ? 'Publisher:'
              : 'Publisher & Creator(s):'
          : creators && creators.length > 0
            ? 'Creator(s):'
            : 'First completed by:';

    const user =
        'submitted_by' in record
            ? record.submitted_by
            : publisher && uniqueCreators.length === 0
              ? publisher
              : creators.length == 1
                ? creators[0]
                : null;

    const totalPeople = uniqueCreators.length + (publisher ? 1 : 0);

    return (
        <div className="flex flex-col">
            {showUserLink && user && totalPeople <= 1 ? (
                <Link
                    className="hidden md:block"
                    to={`/profile/user/$userid`}
                    params={{
                        userid: user.username,
                    }}
                    activeOptions={{ includeSearch: false }}
                >
                    <div className="flex flex-col justify-center items-end gap-1">
                        <p className="text-shadow-3 text-right">
                            {userCountryClanLabel}
                        </p>
                        <h3 className="clamp-[text-sm-2xl-clamp] font-medium transition-transform w-max hover:scale-105 text-right text-shadow-3">
                            {user.global_name}
                        </h3>
                    </div>
                </Link>
            ) : totalPeople > 1 ? (
                <div className="flex flex-col justify-center items-end gap-1">
                    <p className="text-shadow-3 text-right">
                        {userCountryClanLabel}
                    </p>
                    <Tooltip>
                        <TooltipTrigger asChild>
                            <h3 className="clamp-[text-sm-2xl-clamp] font-medium transition-transform w-max text-right text-shadow-3">
                                {totalPeople} people{' '}
                                <InfoIcon
                                    size={28}
                                    className="inline-block ml-1"
                                />
                            </h3>
                        </TooltipTrigger>
                        <TooltipContent
                            side="left"
                            className="bg-background-semilight max-w-lg overflow-visible"
                        >
                            {publisher && (
                                <>
                                    <h3>Publisher:</h3>
                                    <UserTags item={publisher} highContrast />
                                    <Separator className="my-1" />
                                </>
                            )}
                            {creators.length > 0 && (
                                <>
                                    <h3>Creator(s):</h3>
                                    <UserTags items={creators} highContrast />
                                </>
                            )}
                        </TooltipContent>
                    </Tooltip>
                </div>
            ) : showUserRole ? (
                <p className="clamp-[text-sm-2xl-clamp] font-medium text-shadow-3 hidden lg:inline">
                    {userRoleLabel}
                </p>
            ) : (
                <></>
            )}
        </div>
    );
}

function Status({ data }: { data: AvailableDataEntryWithLevel }) {
    return (
        <div
            className={cn(
                'clamp-[px-1-4-clamp] py-1 backdrop-brightness-50 text-shadow-3 border-2 uppercase w-fit clamp-[text-2xs-lg-clamp] rounded-lg font-semibold',
                statusBgClass('status' in data ? data.status : 'Accepted'),
            )}
        >
            <p className="text-shadow-3">
                {'status' in data ? camelToSpaces(data.status) : 'Accepted'}
            </p>
        </div>
    );
}

function LastUpdated({ data }: { data: AvailableRecordSubmissionType }) {
    const localeDateString = useLocaleDateString();
    return (
        <div className="flex flex-col justify-center clamp-[px-0-2-clamp] text-right clamp-[text-2xs-xl-clamp] font-medium text-shadow-3">
            <p className="clamp-[text-2xs-base-clamp] font-thin text-shadow-3 text-nowrap">
                Last updated on:
            </p>
            {localeDateString(data.updated_at)}
        </div>
    );
}

type EntryProps = {
    raw_data: AvailableDataEntry;
    showPoints?: boolean;
    showDetails?: boolean;
    showMobile?: boolean;
    showUserLink?: boolean;
    showUserRole?: boolean;
    showCompletedByUser?: boolean;
    showCompletionLink?: boolean;
    showLastUpdated?: boolean;
    showStatus?: boolean;
    enableThumbnails?: boolean;
    minHeight?: boolean;
    onRowClick?: (data: AvailableDataEntry) => void;
    index?: number;
};

const Entry = memo<EntryProps>(
    ({
        raw_data,
        showPoints,
        showDetails,
        showMobile,
        showUserLink,
        showUserRole,
        showCompletionLink,
        showCompletedByUser,
        showLastUpdated,
        showStatus,
        enableThumbnails,
        minHeight,
        onRowClick,
    }) => {
        const pack = 'tier' in raw_data ? (raw_data as Pack) : null;
        const level = !('tier' in raw_data) ? getLevel(raw_data) : null;

        const thumbnailUrl = !enableThumbnails
            ? ''
            : level
              ? getCardLevelThumbnail(level.level_id)
              : pack
                ? getPackThumbnail(pack.id)
                : '';

        const [imageReady, setImageReady] = useResettableStateWithKey(
            thumbnailUrl,
            () => !thumbnailUrl,
        );

        useEffect(() => {
            if (!thumbnailUrl || imageReady) return;

            const timeout = window.setTimeout(() => {
                setImageReady(true);
            }, 2500);

            return () => {
                window.clearTimeout(timeout);
            };
        }, [thumbnailUrl, imageReady, setImageReady]);

        const entryInfo = pack ? (
            <PackInfo
                pack={pack}
                showPoints={showPoints}
                showDetails={showDetails}
            />
        ) : level ? (
            <LevelInfo
                level={level}
                raw_data={raw_data as AvailableDataEntryWithLevel}
                showPoints={showPoints}
                showDetails={showDetails}
            />
        ) : null;

        const grayscale =
            level &&
            (level.status === 'Legacy' ||
                level.status === 'Removed' ||
                ('completed_by_user' in level && !level.completed_by_user));

        const additionalIcons = (
            <>
                {'mobile' in raw_data && showMobile && (
                    <MobileIcon record={raw_data} />
                )}
                {'video_url' in raw_data &&
                    !('hide_video' in raw_data && raw_data.hide_video) &&
                    showCompletionLink && (
                        <CompletionLinkIcon record={raw_data} />
                    )}
                {'completed_by_user' in raw_data && showCompletedByUser && (
                    <CompletedByUserIcon level={raw_data} />
                )}
            </>
        );

        return (
            <div
                className={cn(
                    'h-full ',
                    'transition-opacity duration-700 ease-in-out',
                    imageReady ? 'opacity-100' : 'opacity-0',
                )}
            >
                <div
                    className={cn(
                        minHeight && 'clamp-[h-14-24-clamp]',
                        'px-2 py-4 flex justify-between w-full items-center gap-2 relative overflow-hidden rounded-xl text-white hover:bg-inherit scale-[0.99] hover:scale-[1.00] cursor-pointer transition-transform',
                        grayscale ? 'grayscale' : '',
                    )}
                    onClick={() => onRowClick?.(raw_data)}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            onRowClick?.(raw_data);
                        }
                    }}
                    role={onRowClick ? 'button' : undefined}
                    tabIndex={onRowClick ? 0 : undefined}
                >
                    {thumbnailUrl ? (
                        <img
                            src={thumbnailUrl}
                            alt=""
                            aria-hidden="true"
                            className="absolute -z-10 inset-0 w-full h-full object-cover"
                            fetchPriority="high"
                            decoding="async"
                            loading="eager"
                            onLoad={() => setImageReady(true)}
                            onError={(e) => {
                                e.currentTarget.style.display = 'none';
                                setImageReady(true);
                            }}
                        />
                    ) : null}
                    {entryInfo}
                    <div className="flex items-center clamp-[gap-2-8-clamp] justify-end clamp-[mr-1-8-clamp]">
                        {!('tier' in raw_data) && (
                            <UserInfo
                                record={raw_data}
                                showUserLink={showUserLink}
                                showUserRole={showUserRole}
                            />
                        )}
                        {additionalIcons}
                        {!('tier' in raw_data) && showStatus && (
                            <Status data={raw_data} />
                        )}
                        {'updated_at' in raw_data && showLastUpdated && (
                            <LastUpdated data={raw_data} />
                        )}
                    </div>
                </div>
            </div>
        );
    },
);
Entry.displayName = 'Entry';

type TableWithThumbnailsFiltersType = {
    search: string | null;
    sort: 'Rank' | 'Date';
    reverse: boolean;
};

const defaultFilters: TableWithThumbnailsFiltersType = {
    search: null,
    sort: 'Rank',
    reverse: false,
};

const TableWithThumbnails: React.FC<TableWithThumbnailsProps> = ({
    data,
    scrollElement,
    showPoints = false,
    showMobile = false,
    showUserLink = false,
    showUserRole = false,
    showCompletionLink = false,
    showCompletedByUser = false,
    showLastUpdated = false,
    showStatus = false,
    minHeight = true,
    enableSearch = false,
    enableSort = false,
    onRowClick,
}) => {
    const { thumbnails_enabled, record_details_enabled } = useSettingsStore();
    const navigate = useNavigate();
    const list = useList();

    const [isSortOpen, setIsSortOpen] = useState(false);
    const [isReverseOpen, setIsReverseOpen] = useState(false);

    const filtersForm = useAppForm({
        defaultValues: defaultFilters,
        listeners: {
            onChangeDebounceMs: 500,
        },
    });

    const filters = useSelector(filtersForm.store, (state) => state.values);

    const [internalScrollElement, setInternalScrollElement] =
        useState<HTMLDivElement | null>(null);

    const hasExternalScrollElement = scrollElement !== undefined;
    const effectiveScrollElement = hasExternalScrollElement
        ? scrollElement
        : internalScrollElement;

    const defaultOnRowClick = useCallback(
        (data: AvailableDataEntry) => {
            if (!('tier' in data)) {
                const level = getLevel(data);
                navigate({
                    to: '/list/$levelid',
                    params: {
                        levelid: formatLevelGDID(level),
                    },
                    search: { list },
                });
            } else {
                navigate({
                    to: '/packs/$packid',
                    params: { packid: data.id },
                    search: { list },
                });
            }
        },
        [navigate, list],
    );

    const filteredData = useMemo(() => {
        if (!filters.search) return data;
        const lowerSearch = filters.search.toLowerCase();
        return data.filter((entry) => {
            return 'tier' in entry
                ? entry?.name.toLowerCase().includes(lowerSearch)
                : getLevel(entry).name.toLowerCase().includes(lowerSearch) ||
                      ('submitted_by' in entry &&
                          entry.submitted_by.global_name
                              .toLowerCase()
                              .includes(lowerSearch)) ||
                      ('publisher' in entry &&
                          entry?.publisher?.global_name
                              .toLowerCase()
                              .includes(lowerSearch)) ||
                      ('creators' in entry &&
                          entry.creators.some((creator) =>
                              creator.global_name
                                  .toLowerCase()
                                  .includes(lowerSearch),
                          ));
        });
    }, [data, filters.search]);

    const sortedData = useMemo(() => {
        const sorted =
            filters.sort === 'Date'
                ? [...filteredData].sort((a, b) => {
                      const aDate =
                          'achieved_at' in a
                              ? a.achieved_at
                              : 'updated_at' in a
                                ? a.updated_at
                                : null;
                      const bDate =
                          'achieved_at' in b
                              ? b.achieved_at
                              : 'updated_at' in b
                                ? b.updated_at
                                : null;

                      if (!aDate && !bDate) return 0;
                      if (!aDate) return 1;
                      if (!bDate) return -1;
                      return bDate.localeCompare(aDate);
                  })
                : [...filteredData];

        return filters.reverse ? sorted.reverse() : sorted;
    }, [filteredData, filters.sort, filters.reverse]);

    const items: EntryProps[] = useMemo(
        () =>
            sortedData.map((entry) => ({
                raw_data: entry,
                showPoints,
                showDetails: record_details_enabled,
                showMobile,
                showUserLink,
                showUserRole,
                showCompletionLink,
                showCompletedByUser,
                showLastUpdated,
                showStatus,
                enableThumbnails: thumbnails_enabled,
                minHeight,
                onRowClick: onRowClick ?? defaultOnRowClick,
            })),
        [
            sortedData,
            showPoints,
            showMobile,
            showUserLink,
            showUserRole,
            showCompletionLink,
            showCompletedByUser,
            showLastUpdated,
            showStatus,
            thumbnails_enabled,
            record_details_enabled,
            minHeight,
            onRowClick,
            defaultOnRowClick,
        ],
    );

    const getItemKey = useCallback(
        (index: number) => items[index].raw_data.id,
        [items],
    );

    const tableContentInner = (
        <>
            {enableSearch && (
                <div className="group relative z-10 px-2 shrink-0">
                    <div className="absolute inset-x-0 top-0 h-8 z-0" />
                    <div
                        className={cn(
                            'pt-2 mb-2 relative z-10 overflow-hidden max-h-0 opacity-0 transition-all duration-300 ease-in-out group-hover:max-h-16 group-hover:opacity-100 focus-within:max-h-16 focus-within:opacity-100',
                            filters.search != defaultFilters.search ||
                                isSortOpen ||
                                isReverseOpen
                                ? 'max-h-16 opacity-100'
                                : '',
                        )}
                    >
                        <div className="flex gap-2 justify-start">
                            <filtersForm.AppField name="search">
                                {(field) => (
                                    <field.SearchField
                                        placeholder="Search..."
                                        hideLabelAndErrors
                                        wrapperClassname="hover:bg-transparent"
                                    />
                                )}
                            </filtersForm.AppField>
                            {enableSort && (
                                <>
                                    <filtersForm.AppField name="sort">
                                        {(field) => (
                                            <field.DropdownField
                                                placeholder="Sort by"
                                                hideLabelAndErrors
                                                options={['Rank', 'Date']}
                                                classname="w-fit bg-background-dark hover:bg-background-dark rounded-xl"
                                                dropdownOpen={isSortOpen}
                                                setDropdownOpen={setIsSortOpen}
                                                preventCloseAutoFocus
                                            />
                                        )}
                                    </filtersForm.AppField>
                                    <filtersForm.AppField name="reverse">
                                        {(field) => (
                                            <field.DropdownBooleanField
                                                placeholder="Reverse"
                                                hideLabelAndErrors
                                                optionEnabled="Ascending"
                                                optionDisabled="Descending"
                                                classname="w-fit bg-background-darker hover:bg-background-darker rounded-xl"
                                                dropdownOpen={isReverseOpen}
                                                setDropdownOpen={
                                                    setIsReverseOpen
                                                }
                                                preventCloseAutoFocus
                                            />
                                        )}
                                    </filtersForm.AppField>
                                </>
                            )}
                        </div>
                    </div>
                </div>
            )}
            {items.length === 0 && (
                <p className="my-6 italic opacity-50 text-center">
                    No results found.
                </p>
            )}
            <VirtualizedScrollArea
                items={items}
                estimateSize={() => 100}
                renderItem={(item) => <Entry {...item} />}
                overscan={10}
                getItemKey={getItemKey}
                className="min-h-0 flex-1 w-full"
                scrollElement={effectiveScrollElement}
            />
        </>
    );

    return hasExternalScrollElement ? (
        <div className="flex h-full min-h-0 w-full flex-col">
            {tableContentInner}
        </div>
    ) : (
        <ScrollArea
            viewportRef={setInternalScrollElement}
            className="h-full min-h-0 w-full"
        >
            {tableContentInner}
        </ScrollArea>
    );
};

export default TableWithThumbnails;

interface PacksTableWithThumbnailsProps extends TableWithThumbnailsProps {
    data: ProfilePack[];
}

export const PacksTableWithThumbnails: React.FC<
    PacksTableWithThumbnailsProps
> = ({ data, showPoints = false, showCompletedByUser = false, onRowClick }) => {
    const list = useList();
    const {
        data: packTiers,
        isLoading: isLoadingTiers,
        isError: isErrorTiers,
        error: errorTiers,
    } = useQuery(packsQuery({ list }));

    const mergedPacks = useMemo(() => {
        if (!packTiers) return data;
        return data
            .map((pack) => {
                const tier = packTiers.find((t) => t.id === pack.tier.id);
                return {
                    ...pack,
                    ...(tier && tier.packs
                        ? tier.packs.find((p) => p.id === pack.id)
                        : {}),
                } as ProfilePack;
            })
            .sort((a, b) => (b?.points ?? 0) - (a?.points ?? 0));
    }, [data, packTiers]);

    return isLoadingTiers ? (
        <Load />
    ) : isErrorTiers ? (
        <ApiErrorComponent {...errorTiers} />
    ) : (
        <TableWithThumbnails
            data={mergedPacks}
            showPoints={showPoints}
            showCompletedByUser={showCompletedByUser}
            onRowClick={onRowClick}
        />
    );
};
